<script setup>
/**
 * PromotionStepsEditor Component
 * Edits step_1 through step_5 for madregot-type promotions
 * Each step has: amount, price, description (all strings)
 */
import { computed } from 'vue'

const props = defineProps({
  step1: { type: Object, default: () => ({}) },
  step2: { type: Object, default: () => ({}) },
  step3: { type: Object, default: () => ({}) },
  step4: { type: Object, default: () => ({}) },
  step5: { type: Object, default: () => ({}) },
})

const emit = defineEmits([
  'update:step1',
  'update:step2',
  'update:step3',
  'update:step4',
  'update:step5',
])

// Create reactive step objects
const steps = computed(() => [
  { key: 'step1', label: 'מדרגה 1', value: props.step1, emit: 'update:step1' },
  { key: 'step2', label: 'מדרגה 2', value: props.step2, emit: 'update:step2' },
  { key: 'step3', label: 'מדרגה 3', value: props.step3, emit: 'update:step3' },
  { key: 'step4', label: 'מדרגה 4', value: props.step4, emit: 'update:step4' },
  { key: 'step5', label: 'מדרגה 5', value: props.step5, emit: 'update:step5' },
])

// Get value for a step field
function getStepValue(step, field) {
  return step.value?.[field] || ''
}

// Update a step field
function updateStepField(stepIndex, field, value) {
  const step = steps.value[stepIndex]
  const newStep = {
    ...step.value,
    [field]: value,
  }
  emit(step.emit, newStep)
}

// Copy step 1 to all other steps
function copyStep1ToAll() {
  const step1Data = props.step1 || {}
  emit('update:step2', { ...step1Data })
  emit('update:step3', { ...step1Data })
  emit('update:step4', { ...step1Data })
  emit('update:step5', { ...step1Data })
}

// Clear a single step
function clearStep(stepIndex) {
  const step = steps.value[stepIndex]
  emit(step.emit, { amount: '', price: '', description: '' })
}
</script>

<template>
  <div class="steps-editor">
    <!-- Header with action -->
    <div class="steps-header">
      <span class="steps-title">מדרגות מחיר</span>
      <button type="button" class="copy-btn" @click="copyStep1ToAll">העתק מדרגה 1 לכולן</button>
    </div>

    <!-- Steps Table -->
    <div class="steps-table">
      <!-- Table Header -->
      <div class="steps-table-header">
        <div class="col-step">מדרגה</div>
        <div class="col-amount">כמות</div>
        <div class="col-price">מחיר</div>
        <div class="col-description">תיאור</div>
        <div class="col-actions"></div>
      </div>

      <!-- Table Rows -->
      <div v-for="(step, index) in steps" :key="step.key" class="steps-table-row">
        <div class="col-step">
          <span class="step-badge">{{ index + 1 }}</span>
        </div>
        <div class="col-amount">
          <input
            type="text"
            class="step-input"
            :value="getStepValue(step, 'amount')"
            placeholder="כמות"
            @input="updateStepField(index, 'amount', $event.target.value)"
          />
        </div>
        <div class="col-price">
          <input
            type="text"
            class="step-input"
            :value="getStepValue(step, 'price')"
            placeholder="מחיר"
            @input="updateStepField(index, 'price', $event.target.value)"
          />
        </div>
        <div class="col-description">
          <input
            type="text"
            class="step-input"
            :value="getStepValue(step, 'description')"
            placeholder="תיאור"
            @input="updateStepField(index, 'description', $event.target.value)"
          />
        </div>
        <div class="col-actions">
          <button type="button" class="clear-btn" title="נקה מדרגה" @click="clearStep(index)">
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <p class="steps-note">הערה: הערכים נשמרים כטקסט (מחרוזת) לתאימות עם המערכת הקיימת</p>
  </div>
</template>

<style scoped>
.steps-editor {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.steps-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.steps-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.copy-btn {
  padding: 6px 12px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid var(--color-primary);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.copy-btn:hover {
  background: rgba(99, 102, 241, 0.2);
}

/* Table */
.steps-table {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
}

.steps-table-header {
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.steps-table-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color-light, var(--border-color));
}

.steps-table-row:last-child {
  border-bottom: none;
}

.col-step {
  width: 60px;
  flex-shrink: 0;
}

.col-amount {
  width: 80px;
  flex-shrink: 0;
}

.col-price {
  width: 80px;
  flex-shrink: 0;
}

.col-description {
  flex: 1;
  min-width: 0;
}

.col-actions {
  width: 32px;
  flex-shrink: 0;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  border-radius: var(--border-radius-full);
}

.step-input {
  width: 100%;
  height: 32px;
  padding: 0 var(--spacing-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  outline: none;
  transition: border-color var(--transition-fast);
}

.step-input:focus {
  border-color: var(--color-primary);
}

.step-input::placeholder {
  color: var(--text-muted);
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.clear-btn:hover {
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.1);
}

.steps-note {
  font-size: 11px;
  color: var(--text-muted);
  margin: 0;
}

/* Responsive */
@media (max-width: 600px) {
  .steps-table-header {
    display: none;
  }

  .steps-table-row {
    flex-wrap: wrap;
    gap: var(--spacing-xs);
  }

  .col-step {
    width: 40px;
  }

  .col-amount,
  .col-price {
    width: calc(50% - 24px);
  }

  .col-description {
    width: 100%;
    order: 1;
    margin-top: var(--spacing-xs);
  }

  .col-actions {
    width: 24px;
  }
}
</style>
